/**
 * Vendor / Supplier Management — subsection rendered INSIDE the Procurement
 * page (not in the sidebar). Backed by /api/vendors/*.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/app/ui-bits";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import Vendor360Drawer from "@/components/app/Vendor360Drawer";
import {
  Plus, Star, Eye, Buildings, Phone, Envelope, MapPin,
  IdentificationBadge, Bank, Warning, ArrowUUpLeft, Trash,
  PaperPlaneTilt, Copy, Clock, CheckCircle, XCircle, Prohibit, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  materials_supplier: "Materials Supplier",
  equipment_supplier: "Equipment Supplier",
  subcontractor: "Subcontractor",
  consultant: "Consultant",
  service_provider: "Service Provider",
  logistics_provider: "Logistics Provider",
  calibration_provider: "Calibration Provider",
  maintenance_vendor: "Maintenance Vendor",
  rental_vendor: "Rental Vendor",
  other: "Other",
};

function VendorForm({ initial, onSaved, projects, meta, vendorId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const empty = {
    name: "", category: "service_provider", classification: "services",
    service_type: "", contact_person: "", phone: "", email: "",
    address: "", country: "", region: "",
    registration_no: "", license_no: "", license_expiry: "",
    tax_id: "", bank_name: "", bank_account: "", swift_iban: "",
    status: "active", preferred: false, notes: "", linked_projects: [],
  };
  const [form, setForm] = useState(initial || empty);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (initial) setForm({ ...empty, ...initial }); }, [initial]);

  const submit = async () => {
    if (!form.name || form.name.length < 2) return toast.error("Vendor name required");
    setBusy(true);
    try {
      const payload = { ...form };
      if (vendorId) {
        const { data } = await api.patch(`/vendors/${vendorId}`, payload);
        if (data?.bank_change_pending) {
          toast.info(data.bank_change_message || "Bank detail changes routed for approval (maker-checker)", { duration: 7000 });
        }
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success(`Vendor "${form.name}" created`);
      }
      onSaved?.();
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {initial ? (
          <Button size="sm" variant="outline" data-testid={`vendor-edit-${vendorId}`}>Edit</Button>
        ) : (
          <Button className="btn-primary-blue" size="sm" data-testid="vendor-new-btn">
            <Plus size={14} className="mr-2" /> New vendor
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{vendorId ? "Edit vendor" : "New vendor"}</DialogTitle>
          <DialogDescription>Vendor / supplier master record — links into procurement, projects, finance and compliance.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Vendor name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="vendor-name" />
          </div>
          <div><Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="vendor-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Classification</Label>
            <Select value={form.classification} onValueChange={(v) => set("classification", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.classifications || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Service type / detail</Label>
            <Input value={form.service_type} onChange={(e) => set("service_type", e.target.value)}
              placeholder="e.g. structural steel, surveying, calibration of total stations…" />
          </div>
          <div><Label>Contact person</Label>
            <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
          </div>
          <div><Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div><Label>Email</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div><Label>Country</Label>
            <Input value={form.country} onChange={(e) => set("country", e.target.value)} />
          </div>
          <div className="sm:col-span-2"><Label>Address</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div><Label>Registration no.</Label>
            <Input value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} />
          </div>
          <div><Label>License no.</Label>
            <Input value={form.license_no} onChange={(e) => set("license_no", e.target.value)} />
          </div>
          <div><Label>License expiry</Label>
            <Input type="date" value={form.license_expiry || ""} onChange={(e) => set("license_expiry", e.target.value)} />
          </div>
          <div><Label>Tax ID</Label>
            <Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} />
          </div>
          <div><Label>Bank name</Label>
            <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} />
          </div>
          <div><Label>Bank account</Label>
            <Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} />
          </div>
          <div><Label>SWIFT / IBAN</Label>
            <Input value={form.swift_iban} onChange={(e) => set("swift_iban", e.target.value)} />
          </div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Preferred</Label>
            <Select value={form.preferred ? "yes" : "no"}
              onValueChange={(v) => set("preferred", v === "yes")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes — preferred</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Internal notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          {vendorId && (
            <div className="sm:col-span-2">
              <Label>Reason for this change (audited)</Label>
              <Input value={form.change_reason || ""} onChange={(e) => set("change_reason", e.target.value)}
                placeholder="e.g. updated contact details per vendor letter of 12 Jun" data-testid="vendor-change-reason" />
              <p className="mt-1 text-[10px] text-amber-700">
                Bank detail changes (bank name / account / SWIFT) are routed for approval before they apply.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="btn-primary-blue" data-testid="vendor-save">
            {busy ? "Saving…" : "Save vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VendorDetail({ vendorId, onClose, refresh, isAdmin }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (vendorId) api.get(`/vendors/${vendorId}`).then((r) => setData(r.data));
  }, [vendorId]);
  if (!vendorId) return null;

  const blacklist = async () => {
    const reason = window.prompt("Reason for blacklisting:");
    if (!reason) return;
    try {
      await api.post(`/vendors/${vendorId}/blacklist`, { reason });
      toast.success("Vendor blacklisted");
      refresh?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async () => {
    try {
      await api.post(`/vendors/${vendorId}/restore`);
      toast.success("Vendor restored");
      refresh?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archiveAction = async (action) => {
    const reason = window.prompt(`Reason to ${action} this vendor:`);
    if (!reason) return;
    try {
      await api.post(`/vendors/${vendorId}/archive`, { action, reason });
      toast.success(`Vendor ${action}d`);
      refresh?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const permanentDelete = async () => {
    const reason = window.prompt("Permanent deletion is ONLY for duplicates/errors with no history.\nMandatory reason:");
    if (!reason) return;
    if (!window.confirm(`Permanently delete "${data?.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vendors/${vendorId}`, { params: { reason } });
      toast.success("Vendor permanently deleted");
      refresh?.(); onClose?.();
    } catch (e) { toast.error(formatApiError(e), { duration: 8000 }); }
  };

  return (
    <Dialog open={!!vendorId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Buildings size={18} className="text-slate-700" weight="duotone" />
            <DialogTitle className="font-display">{data?.name || "Loading…"}</DialogTitle>
            {data && <StatusBadge status={data.status} />}
            {data?.preferred && <Star size={14} weight="fill" className="text-amber-500" />}
          </div>
          <DialogDescription>{data?.vendor_no} · {CATEGORY_LABELS[data?.category] || data?.category}</DialogDescription>
        </DialogHeader>

        {data && (
          <>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <F k="Service type" v={data.service_type} />
              <F k="Classification" v={data.classification} />
              <F k="Contact" v={data.contact_person} icon={<IdentificationBadge size={11} />} />
              <F k="Phone" v={data.phone} icon={<Phone size={11} />} />
              <F k="Email" v={data.email} icon={<Envelope size={11} />} />
              <F k="Country" v={data.country} />
              <F k="Address" v={data.address} icon={<MapPin size={11} />} full />
              <F k="Registration no." v={data.registration_no} />
              <F k="License" v={data.license_no} />
              <F k="License expiry" v={data.license_expiry ? formatDate(data.license_expiry) : "—"} />
              <F k="Tax ID" v={data.tax_id} />
              <F k="Bank" v={[data.bank_name, data.bank_account].filter(Boolean).join(" · ") || "—"} icon={<Bank size={11} />} full />
              <F k="Notes" v={data.notes} full />
            </div>

            {data.linked_procurement?.length > 0 && (
              <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Linked procurement records</div>
                <ul className="mt-2 space-y-1 text-xs">
                  {data.linked_procurement.map((p) => (
                    <li key={p.id} className="flex justify-between">
                      <span>{p.title}</span>
                      <span className="text-slate-500">{p.stream} · {p.current_phase} · {p.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.status === "blacklisted" && data.blacklist_reason && (
              <div className="mt-3 rounded border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                <Warning size={12} className="inline mr-1" /> <b>Blacklisted:</b> {data.blacklist_reason}
              </div>
            )}
          </>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {data && <Vendor360Drawer vendorId={data.id} vendorName={data.name} canEdit={isAdmin} />}
          {data && <AuditTrailDrawer entity="vendor" entityId={data.id} label="Audit" />}
          {isAdmin && data && !["archived", "inactive", "suspended", "blacklisted"].includes(data.status) && (
            <>
              <Button variant="outline" size="sm" onClick={() => archiveAction("archive")} data-testid="vendor-archive">Archive</Button>
              <Button variant="outline" size="sm" onClick={() => archiveAction("suspend")} data-testid="vendor-suspend">Suspend</Button>
            </>
          )}
          {isAdmin && data && ["archived", "inactive", "suspended"].includes(data.status) && (
            <Button variant="outline" size="sm" onClick={restore} data-testid="vendor-reactivate">Reactivate</Button>
          )}
          {isAdmin && data && (
            <Button variant="outline" size="sm" className="text-red-700" onClick={permanentDelete} data-testid="vendor-delete">
              <Trash size={12} className="mr-1" /> Delete
            </Button>
          )}
          {isAdmin && data && data.status !== "blacklisted" && (
            <Button variant="outline" onClick={blacklist} data-testid="vendor-blacklist">
              <Warning size={12} className="mr-1.5 text-red-700" /> Blacklist
            </Button>
          )}
          {isAdmin && data && data.status === "blacklisted" && (
            <Button variant="outline" onClick={restore} data-testid="vendor-restore">
              <ArrowUUpLeft size={12} className="mr-1.5" /> Restore
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ k, v, icon, full }) {
  return (
    <div className={`${full ? "sm:col-span-2" : ""} border-b border-slate-100 pb-1.5`}>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {icon} {k}
      </div>
      <div className="mt-0.5 text-sm text-slate-900">{v || "—"}</div>
    </div>
  );
}

export default function VendorsPanel() {
  const { user } = useAuth();
  const isAdmin = hasMinRole(user, "admin");
  const canEdit = hasMinRole(user, "manager");
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: "all", status: "all", preferred: "all" });
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("registry"); // registry | onboarding

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category !== "all") params.set("category", filters.category);
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.preferred === "yes") params.set("preferred", "true");
      if (search) params.set("search", search);
      const [m, l] = await Promise.all([
        api.get("/vendors/meta"),
        api.get(`/vendors?${params.toString()}`),
      ]);
      setMeta(m.data);
      setItems(l.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters, search]);

  return (
    <div className="space-y-4" data-testid="vendors-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Vendor / Supplier Management</h3>
          <p className="text-xs text-slate-500">
            Central register of all DESCON vendors, suppliers, subcontractors, consultants, and service providers.
          </p>
        </div>
        <div className="flex gap-2">
          {canEdit && <InviteVendorDialog onChanged={() => { load(); }} />}
          {canEdit && <VendorForm onSaved={load} meta={meta} />}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        <button
          type="button" onClick={() => setView("registry")}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${view === "registry" ? "bg-[#0f1e42] text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="vendors-sub-registry"
        >Vendor Register</button>
        <button
          type="button" onClick={() => setView("onboarding")}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${view === "onboarding" ? "bg-[#0f1e42] text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="vendors-sub-onboarding"
        >Onboarding</button>
      </div>

      {view === "onboarding" ? (
        <OnboardingTab canEdit={canEdit} />
      ) : (
        <RegistryView
          items={items} loading={loading} canEdit={canEdit}
          search={search} setSearch={setSearch}
          filters={filters} setFilters={setFilters}
          meta={meta} setSelectedId={setSelectedId}
        />
      )}

      <VendorDetail vendorId={selectedId} onClose={() => setSelectedId(null)}
        refresh={load} isAdmin={isAdmin} />
    </div>
  );
}

function RegistryView({ items, loading, canEdit, search, setSearch, filters, setFilters, meta, setSelectedId }) {
  return (
    <>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Search</div>
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, vendor no., contact, email…" data-testid="vendors-search" />
        </div>
        <div className="min-w-[170px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Category</div>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="vendors-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[150px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Status</div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="vendors-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[120px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Preferred</div>
          <Select value={filters.preferred} onValueChange={(v) => setFilters({ ...filters, preferred: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Preferred only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Buildings size={28} className="mx-auto text-slate-300" />
            <div className="mt-2 font-display text-sm font-semibold text-slate-900">No vendors yet</div>
            <div className="text-xs text-slate-500">
              {canEdit ? "Click 'New vendor' above to register the first one." : "No vendors registered."}
            </div>
          </div>
        ) : (
          <>
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Vendor</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Country</th>
                  <th className="px-5 py-3">Contact</th>
                  <th className="px-5 py-3">License</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((v) => (
                  <tr key={v.id} className="text-sm cursor-pointer hover:bg-slate-50 transition"
                      onClick={() => setSelectedId(v.id)} data-testid={`vendor-row-${v.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                        {v.preferred && <Star size={12} weight="fill" className="text-amber-500" />}
                        {v.name}
                      </div>
                      <div className="font-mono-alt text-[10px] text-slate-500">
                        {v.vendor_no}
                        {v.source_submission_id && <span className="ml-1 text-emerald-600">· onboarded</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs">{CATEGORY_LABELS[v.category] || v.category}</td>
                    <td className="px-5 py-3 text-xs">{v.country || "—"}</td>
                    <td className="px-5 py-3 text-xs">
                      <div>{v.contact_person || "—"}</div>
                      <div className="text-[11px] text-slate-500">{v.email || v.phone || ""}</div>
                    </td>
                    <td className="px-5 py-3 text-xs">{v.license_no || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={v.status} /></td>
                    <td className="px-5 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedId(v.id); }}
                        data-testid={`vendor-open-${v.id}`}>
                        <Eye size={12} className="mr-1" /> Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-slate-100 md:hidden">
              {items.map((v) => (
                <li key={v.id} className="p-4 cursor-pointer hover:bg-slate-50 transition"
                    onClick={() => setSelectedId(v.id)} data-testid={`vendor-mrow-${v.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                        {v.preferred && <Star size={12} weight="fill" className="text-amber-500" />}
                        {v.name}
                      </div>
                      <div className="text-[11px] text-slate-500">{CATEGORY_LABELS[v.category] || v.category} · {v.country || "—"}</div>
                    </div>
                    <StatusBadge status={v.status} />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">{v.contact_person || "—"} · {v.email || v.phone || ""}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
}

// ── Onboarding: Invite dialog ─────────────────────────────────────────
function InviteVendorDialog({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor_name_hint: "", contact_email_hint: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [invitation, setInvitation] = useState(null);

  const linkFor = (token) => `${window.location.origin}/admin/vendor-onboarding/${token}`;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vendor-onboarding/invitations", form);
      setInvitation(data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    catch { toast.error("Could not copy — please select the link manually"); }
  };

  const reset = () => { setInvitation(null); setForm({ vendor_name_hint: "", contact_email_hint: "", notes: "" }); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="vendors-invite-btn">
          <PaperPlaneTilt size={14} className="mr-1" /> Invite Vendor
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a vendor / supplier</DialogTitle>
          <DialogDescription>
            Generate a secure 7-day onboarding link. Send it to the vendor — they fill the registration form externally.
          </DialogDescription>
        </DialogHeader>

        {!invitation ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Vendor name (hint, optional)</Label>
              <Input value={form.vendor_name_hint} onChange={(e) => setForm({ ...form, vendor_name_hint: e.target.value })} data-testid="invite-name-hint" />
            </div>
            <div>
              <Label className="text-xs">Vendor email (hint, optional)</Label>
              <Input value={form.contact_email_hint} onChange={(e) => setForm({ ...form, contact_email_hint: e.target.value })} data-testid="invite-email-hint" />
            </div>
            <div>
              <Label className="text-xs">Internal notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invite-notes" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              <CheckCircle size={14} className="inline mr-1.5" />
              Link generated. Send it to the vendor — it expires in 7 days.
            </div>
            <div>
              <Label className="text-xs">Onboarding link</Label>
              <div className="flex gap-2">
                <Input readOnly value={linkFor(invitation.token)} data-testid="invite-link" />
                <Button variant="outline" onClick={() => copy(linkFor(invitation.token))} data-testid="invite-copy">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
            </div>
            <div className="text-[11px] text-slate-500">
              Expires: {new Date(invitation.expires_at).toLocaleString()}
            </div>
          </div>
        )}

        <DialogFooter>
          {!invitation ? (
            <Button onClick={create} disabled={busy} className="btn-primary-blue" data-testid="invite-generate">
              {busy ? "Generating…" : "Generate link"}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Onboarding tab (invitations + submissions) ────────────────────────
function OnboardingTab({ canEdit }) {
  const [invitations, setInvitations] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [openSub, setOpenSub] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, sub, sum] = await Promise.all([
        api.get("/vendor-onboarding/invitations"),
        api.get("/vendor-onboarding/submissions"),
        api.get("/vendor-onboarding/summary"),
      ]);
      setInvitations(inv.data.items || []);
      setSubmissions(sub.data.items || []);
      setSummary(sum.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this onboarding link? The vendor will no longer be able to submit.")) return;
    try {
      await api.post(`/vendor-onboarding/invitations/${id}/revoke`);
      toast.success("Link revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const linkFor = (token) => `${window.location.origin}/admin/vendor-onboarding/${token}`;
  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div className="space-y-5" data-testid="vendors-onboarding-tab">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Tile label="Active links"      value={summary.active_invitations || 0} tone="blue" />
        <Tile label="Pending review"    value={summary.pending_submissions || 0} tone={summary.pending_submissions ? "amber" : "slate"} />
        <Tile label="Approved"          value={summary.approved || 0} tone="emerald" />
        <Tile label="Rejected"          value={summary.rejected || 0} tone={summary.rejected ? "red" : "slate"} />
        <Tile label="Expired links"     value={summary.expired_invitations || 0} tone="slate" />
      </div>

      {/* Pending submissions */}
      <section className="dc-card p-4">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Submitted vendor profiles</h3>
        {loading ? <div className="p-4 text-center text-xs text-slate-500">Loading…</div>
          : submissions.length === 0 ? <div className="p-6 text-center text-xs text-slate-400">No submissions yet.</div>
          : (
            <ul className="divide-y divide-slate-100">
              {submissions.map((s) => (
                <li key={s.id} className="py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 px-2 rounded"
                    onClick={() => setOpenSub(s.id)} data-testid={`sub-row-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-900 truncate">{s.company_name}</div>
                    <div className="text-[11px] text-slate-500">
                      {s.vendor_type?.replace(/_/g, " ")} · {s.email || s.contact_person || "no contact"} · submitted {formatDate(s.submitted_at)}
                    </div>
                  </div>
                  <SubmissionStatusBadge status={s.status} />
                  <ArrowRight size={14} className="text-slate-400" />
                </li>
              ))}
            </ul>
          )}
      </section>

      {/* Invitations */}
      <section className="dc-card p-4">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Onboarding invitations</h3>
        {invitations.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-400">No invitations generated yet. Use "Invite Vendor" above.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invitations.map((inv) => (
              <li key={inv.id} className="py-3 flex items-center gap-3" data-testid={`inv-row-${inv.id}`}>
                <InvitationStatusIcon status={inv.status} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 truncate">
                    {inv.vendor_name_hint || <span className="text-slate-400 italic">No name hint</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {inv.contact_email_hint || "—"} · by {inv.created_by_name || inv.created_by} · created {formatDate(inv.created_at)}
                    {inv.status === "active" && inv.expires_at && (
                      <> · <Clock size={11} className="inline mb-0.5" /> expires {formatDate(inv.expires_at)}</>
                    )}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${
                  inv.status === "active" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                  inv.status === "submitted" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                  inv.status === "expired" ? "bg-slate-100 text-slate-600 border border-slate-200" :
                  "bg-rose-50 text-rose-700 border border-rose-200"
                }`}>{inv.status}</span>
                {inv.status === "active" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => copy(linkFor(inv.token))} data-testid={`inv-copy-${inv.id}`}>
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="outline" className="text-rose-600" onClick={() => revoke(inv.id)} data-testid={`inv-revoke-${inv.id}`}>
                        <Prohibit size={12} className="mr-1" /> Revoke
                      </Button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <SubmissionDrawer submissionId={openSub} onClose={() => setOpenSub(null)} refresh={load} />
    </div>
  );
}

function Tile({ label, value, tone = "slate" }) {
  const border = { blue: "border-l-[#1E5CBA]", emerald: "border-l-emerald-500", amber: "border-l-amber-500", red: "border-l-rose-500", slate: "border-l-slate-300" }[tone];
  const text = { blue: "text-[#1E5CBA]", emerald: "text-emerald-700", amber: "text-amber-700", red: "text-rose-700", slate: "text-slate-700" }[tone];
  return (
    <div className={`dc-card p-3 border-l-4 ${border}`}>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${text}`}>{value}</div>
    </div>
  );
}

function InvitationStatusIcon({ status }) {
  if (status === "active")    return <PaperPlaneTilt size={16} className="text-blue-600" />;
  if (status === "submitted") return <CheckCircle size={16} weight="fill" className="text-emerald-600" />;
  if (status === "expired")   return <Clock size={16} className="text-slate-500" />;
  return <Prohibit size={16} className="text-rose-500" />;
}

const SUBMISSION_STATUS_META = {
  submitted:          { label: "Submitted",       tone: "bg-blue-50 text-blue-700 border-blue-200" },
  procurement_review: { label: "Procurement",     tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  info_requested:     { label: "Info requested",  tone: "bg-amber-50 text-amber-700 border-amber-200" },
  compliance_review:  { label: "Compliance",      tone: "bg-violet-50 text-violet-700 border-violet-200" },
  finance_review:     { label: "Finance",         tone: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  approved:           { label: "Approved",        tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:           { label: "Rejected",        tone: "bg-rose-50 text-rose-700 border-rose-200" },
};
function SubmissionStatusBadge({ status }) {
  const m = SUBMISSION_STATUS_META[status] || { label: status, tone: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-medium ${m.tone}`}>{m.label}</span>;
}

// ── Submission detail drawer with stage review actions ────────────────
function SubmissionDrawer({ submissionId, onClose, refresh }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!submissionId) { setData(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/vendor-onboarding/submissions/${submissionId}`);
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [submissionId]);

  if (!submissionId) return null;

  const stage =
    data?.status === "submitted" || data?.status === "procurement_review" || data?.status === "info_requested" ? "procurement-review" :
    data?.status === "compliance_review" ? "compliance-review" :
    data?.status === "finance_review" ? "finance-review" : null;

  const stageLabel = {
    "procurement-review": "Procurement review",
    "compliance-review":  "Compliance review",
    "finance-review":     "Finance review",
  }[stage];

  const act = async (decision) => {
    if (!stage) return;
    setBusy(true);
    try {
      await api.post(`/vendor-onboarding/submissions/${submissionId}/${stage}`, { decision, comment });
      toast.success(`Decision recorded: ${decision}`);
      setComment("");
      const { data: fresh } = await api.get(`/vendor-onboarding/submissions/${submissionId}`);
      setData(fresh);
      refresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.post(`/vendor-onboarding/submissions/${submissionId}/reopen`);
      toast.success("Submission reopened — invitation extended 7 days");
      const { data: fresh } = await api.get(`/vendor-onboarding/submissions/${submissionId}`);
      setData(fresh);
      refresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadDoc = async (i, name) => {
    try {
      const { data } = await api.get(`/vendor-onboarding/submissions/${submissionId}/documents/${i}`);
      const blob = b64ToBlob(data.data_base64, data.content_type || "application/octet-stream");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || data.name || `document-${i}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="submission-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {data?.company_name || "Vendor submission"}
            {data && <SubmissionStatusBadge status={data.status} />}
          </DialogTitle>
          <DialogDescription>
            Submitted via onboarding link · {data ? formatDate(data.submitted_at) : ""}
          </DialogDescription>
        </DialogHeader>

        {!data ? <div className="p-6 text-center text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-4">
            <SectionRow title="Company">
              <Cell label="Type" v={data.vendor_type?.replace(/_/g, " ")} />
              <Cell label="Category" v={data.category} />
              <Cell label="Country / region" v={[data.country, data.region].filter(Boolean).join(" · ")} />
              <Cell label="Address" v={data.address} wide />
              <Cell label="Summary" v={data.services_summary} wide />
            </SectionRow>
            <SectionRow title="Contact">
              <Cell label="Person" v={data.contact_person} />
              <Cell label="Email" v={data.email} />
              <Cell label="Phone" v={data.phone} />
            </SectionRow>
            <SectionRow title="Registration & tax">
              <Cell label="Business reg." v={data.registration_no} />
              <Cell label="Tax / TIN" v={data.tax_id} />
            </SectionRow>
            <SectionRow title="Banking">
              <Cell label="Bank" v={data.bank_name} />
              <Cell label="Account" v={data.bank_account} />
              <Cell label="SWIFT / IBAN" v={data.swift_iban} />
              <Cell label="Mobile money" v={[data.mobile_money_provider, data.mobile_money_number].filter(Boolean).join(" · ")} />
            </SectionRow>
            {(data.company_profile || data.past_experience || data.references) && (
              <SectionRow title="Profile">
                <Cell label="Company profile" v={data.company_profile} wide />
                <Cell label="Past experience" v={data.past_experience} wide />
                <Cell label="References" v={data.references} wide />
              </SectionRow>
            )}

            <section>
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-2">Documents</div>
              {(data.documents || []).length === 0 ? (
                <div className="text-xs text-slate-400">No attachments.</div>
              ) : (
                <ul className="space-y-1">
                  {data.documents.map((d, i) => (
                    <li key={i} className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
                      <span className="flex-1 truncate">{d.name}</span>
                      <span className="text-[11px] text-slate-500 tabular-nums">{Math.round((d.size_bytes || 0) / 1024)} KB</span>
                      <Button size="sm" variant="outline" onClick={() => downloadDoc(i, d.name)} data-testid={`sub-doc-${i}`}>Download</Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Review history */}
            {(data.comments || []).length > 0 && (
              <section>
                <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-2">Review history</div>
                <ul className="space-y-2">
                  {data.comments.map((c, i) => (
                    <li key={i} className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{c.stage.replace(/_/g, " ")}</span>
                        <span className={c.decision === "approve" ? "text-emerald-700" : c.decision === "reject" ? "text-rose-700" : "text-amber-700"}>{c.decision}</span>
                        <span className="text-slate-500">· {c.by_name || c.by}</span>
                        <span className="ml-auto text-slate-400">{formatDate(c.at)}</span>
                      </div>
                      {c.comment && <div className="text-slate-700 mt-1">{c.comment}</div>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Action panel */}
            {stage && (
              <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="font-display text-sm font-semibold text-slate-900 mb-2">{stageLabel}</div>
                <Textarea rows={3} placeholder="Comment (optional, shared with internal reviewers)"
                  value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={() => act("approve")} disabled={busy} className="btn-primary-blue" data-testid="review-approve">
                    <CheckCircle size={14} className="mr-1" /> Approve · next stage
                  </Button>
                  <Button variant="outline" onClick={() => act("request_info")} disabled={busy} className="text-amber-700" data-testid="review-request-info">
                    Request more info
                  </Button>
                  <Button variant="outline" onClick={() => act("reject")} disabled={busy} className="text-rose-700" data-testid="review-reject">
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </section>
            )}

            {(data.status === "rejected" || data.status === "info_requested") && (
              <Button variant="outline" onClick={reopen} disabled={busy} data-testid="review-reopen">
                <ArrowUUpLeft size={14} className="mr-1" /> Reopen submission (extends invitation 7 days)
              </Button>
            )}

            {data.status === "approved" && data.linked_vendor_id && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                <CheckCircle size={14} className="inline mr-1.5" />
                Approved — vendor record created. The vendor is now active in the Register.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionRow({ title, children }) {
  return (
    <section>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </section>
  );
}
function Cell({ label, v, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 whitespace-pre-wrap break-words">{v || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function b64ToBlob(b64, contentType) {
  const byteChars = atob(b64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}
